import React, { useCallback } from 'react';

import PropTypes from 'prop-types';

import {
    LinkIcon,
    List,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { psxVDPVHRClick } from 'reaxl-analytics-handlers';
import { EmailCTA } from 'reaxl-email';

import { inventoryProductClick } from '@/analytics/inventoryAnalyticsHandlers';

import Card from './Card';
import CardContentWithAnalytics from './CardContentWithAnalytics';
import { cardHeaders, cardImages } from './staticContent';

// The Data Prioritization following the list.
const vhrLabels = {
    SALVAGE_TITLE: 'Salvage Title',
    FRAME_DAMAGE: 'Frame Damage',
    FLOOD_WATER_DAMAGE: 'Flood Damage',
    ACCIDENTS_REPORTED: 'Accident or Damage Reported',
    NO_ACCIDENTS_REPORTED: 'No Accident or Damage Reported',
    NO_ODOMETER_CHECK: 'No Odometer Check',
    // VEHICLE_USE: 'Vehicle Use Type',
    ONE_OWNER: 'Single Owner',
    NO_ONE_OWNER: 'Multiple Owners',
    OPEN_RECALLS_REPORTED: 'At Least 1 Open Recall Reported',
    NO_OPEN_RECALLS_REPORTED: 'No Recalls Reported',
};

// TODO: re-check link labels in BE when removing FF.
const vhrUpdateLinkLabels = {
    EXPERIAN_RETAIL: 'Get a Vehicle History Report',
    CARFAX_RETAIL: 'Get a CARFAX History Report',
    EXPERIAN_APPENDED: 'View the Free Vehicle History Report',
    CARFAX_APPENDED: 'View the Free CARFAX Report',
};

const vhrLinkLabelsMap = {
    CARFAX_APPENDED: 'carfaxAppended',
    EXPERIAN_APPENDED: 'experianAppended',
};

export const getPsxVendor = (tileType) => {
    switch (tileType) {
        case 'EXPERIAN_RETAIL':
        case 'EXPERIAN_APPENDED':
            return 'autocheckps';
        case 'CARFAX_RETAIL':
        case 'CARFAX_APPENDED':
            return 'carfaxps';
        default:
            return '';
    }
};

function VhrCard({
    index,
    isPsx = false,
    vhrTile = {},
    vhrPreview,
    onEmailCTAClick,
    overrideExperianTile,
    removeVhrCarfaxTile,
    listingId,
    handleContentImpression = () => { },
    isInverse,
    ...rest
}) {
    const { tileType, epn } = vhrTile;

    const vhrLink = vhrTile.link?.href;
    const isCarfaxTile = vhrTile?.tileType?.split('_')[0] === 'CARFAX';
    const vhrLinkLabel = vhrLinkLabelsMap[tileType];
    const isLeadGateEnabled = typeof onEmailCTAClick === 'function' && !!vhrLinkLabel;

    const handleClick = useCallback((event) => {
        if (isPsx) {
            sendClick(psxVDPVHRClick, event, { listingId, vendor: getPsxVendor(tileType), epn });
        } else {
            sendClick(inventoryProductClick, event, { epn, tileType });
        }

        if (isCarfaxTile && removeVhrCarfaxTile) {
            // decode href and open link in new window
            window.open(atob(vhrLink), '_blank');
        }
    }, [isPsx, isCarfaxTile, removeVhrCarfaxTile, listingId, tileType, epn, vhrLink]);

    const handleContentImpressionWithTile = () => handleContentImpression(vhrTile);

    const vhrItems = Object.keys(vhrLabels).filter((item) => (vhrPreview).includes(item)).map((item) => vhrLabels[item]).slice(0, 3);

    // Check if this is an Experian tile
    const isExperianTile = vhrTile?.tileType?.split('_')[0] === 'EXPERIAN';
    const shouldOverrideExperianTile = overrideExperianTile && isExperianTile;

    let image;
    let imageProps = {};
    if (shouldOverrideExperianTile) {
        image = isInverse ? cardImages.EXPERIAN_KBB_INVERSE : cardImages.EXPERIAN_KBB;
        imageProps = { height: 72, width: 90 };
    } else if (isExperianTile) {
        image = isInverse ? cardImages.EXPERIAN_INVERSE : cardImages.EXPERIAN;
        imageProps = { height: 38, width: 68 };
    } else { // CARFAX
        image = vhrTile?.image?.src;
    }

    /* We only want to remove the tile/link if it's a Carfax tile AND the Optimizely flag is turned on for VDP */
    /* We always want to show Experian tiles. We should also show Carfax tiles if the experiment is off */

    const showTile = !(removeVhrCarfaxTile && isCarfaxTile);

    // Omit the label from vhrTile.link to avoid LinkIcon renders 2 labels.
    const vhrLinkIconProps = (({ label, offsite, ...others }) => others)(vhrTile.link);
    const vhrProps = (isCarfaxTile && removeVhrCarfaxTile) ? {} : vhrLinkIconProps;

    const handleImageTileClick = (event) => (isLeadGateEnabled ? onEmailCTAClick('vehicleHistoryReport') : handleClick(event));

    const renderVhrLink = () => {
        if (!(vhrTile.link)) return null;

        if (isLeadGateEnabled) {
            return (
                <EmailCTA
                    context="vehicleHistoryReport"
                    key={vhrTile.tile + index}
                    labelKey={vhrLinkLabel}
                    onClick={onEmailCTAClick}
                    type={isInverse ? 'link-decorated-inverse' : 'link-decorated'}
                />
            );
        }

        return (
            <LinkIcon
                key={vhrTile.tile + index}
                {...vhrProps}
                inverse={isInverse}
                onClick={handleClick}
                rel="nofollow noopener"
                target="_blank"
            >
                {vhrUpdateLinkLabels[vhrTile.tileType]}
            </LinkIcon>
        );
    };

    return (
        <Card
            id="vhr"
            heading={cardHeaders.VEHICLE_HISTORY_REPORT}
            image={image}
            imageProps={imageProps}
            isInverse={isInverse}
            handleClick={handleImageTileClick}
            linkLabel={vhrUpdateLinkLabels[vhrTile.tileType]}
            showTile={showTile}
            {...(!isLeadGateEnabled && { link: vhrTile.link?.href })}
            {...rest}
        >
            <CardContentWithAnalytics
                cardType={`vhr-${tileType?.toLowerCase()}`}
                handleContentImpression={handleContentImpressionWithTile}
                listingId={listingId}
            >
                {vhrItems.length > 0 && (
                    <List
                        className="margin-bottom-2"
                        items={vhrItems}
                    />
                )}
                {renderVhrLink()}
            </CardContentWithAnalytics>
        </Card>
    );
}

VhrCard.propTypes = {
    isInverse: PropTypes.bool,
};

export default VhrCard;
